import React, { Suspense, useRef, useState, useEffect } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, useGLTF, useAnimations } from "@react-three/drei";

interface ModelProps {
  modelUrl: string;
  scale?: number;
}

const Model: React.FC<ModelProps> = ({ modelUrl, scale = 0.05 }) => {
  // Reduced scale by half
  const { scene, animations } = useGLTF(modelUrl);
  const ref = useRef<any>();
  const { actions, names } = useAnimations(animations, ref);
  const [hovered, setHovered] = useState(false);
  const [rotationDirection, setRotationDirection] = useState(1);
  const rotationSpeed = 0.005;
  const rotationLimit = 0.3; // Limit for back and forth rotation

  // Auto-rotate the model back and forth
  useFrame(() => {
    if (ref.current) {
      ref.current.rotation.y += rotationSpeed * rotationDirection;
      if (
        ref.current.rotation.y > rotationLimit ||
        ref.current.rotation.y < -rotationLimit
      ) {
        setRotationDirection(-rotationDirection);
      }
    }
  });

  // Hover effect - slightly scales up the model when hovered
  useFrame(() => {
    if (ref.current) {
      ref.current.scale.set(
        hovered ? scale * 1.1 : scale,
        hovered ? scale * 1.1 : scale,
        hovered ? scale * 1.1 : scale,
      );
    }
  });

  // Play the waving hand animation (index 3)
  useEffect(() => {
    if (actions && names.length > 3) {
      const waveAction = actions[names[3]]; // Index 3 for the waving animation
      if (waveAction) {
        waveAction.play();
      }
    }
  }, [actions, names]);

  return (
    <primitive
      ref={ref}
      object={scene}
      scale={scale} // Scale of the model
      onPointerOver={() => setHovered(true)}
      onPointerOut={() => setHovered(false)}
    />
  );
};

const ModelViewer: React.FC<ModelProps> = ({ modelUrl, scale = 0.05 }) => {
  // Reduced scale by half
  return (
    <Canvas>
      <ambientLight intensity={1} /> // Lighting setting
      <directionalLight position={[10, 10, 10]} intensity={1} /> // Directional
      light setting
      <OrbitControls
        enableZoom={true}
        enablePan={true}
        minDistance={5}
        maxDistance={9} // Controls the zoom distance
      />
      <Suspense fallback={null}>
        <Model modelUrl={modelUrl} scale={scale} /> // Pass the scale to Model
        component
      </Suspense>
    </Canvas>
  );
};

export default ModelViewer;
